<template>
  <vue-final-popover
    v-slot="{ close }"
    v-bind="$attrs"
    :esc-to-close="true"
    :position="position"
    :target="target"
    :z-index-auto="zIndexAuto"
    :z-index-base="zIndexBase"
    :z-index="zIndex"
    :width="width"
    :height="height"
    :transition="transition"
  >
    <div :class="['popover__content', popoverClass]" :style="styles">
      <slot name="content" :close="close" />
    </div>
  </vue-final-popover>
</template>

<script>
import { computed } from 'vue';

/**
 * Vị trí xuất hiện so với target element
 */
export const MsPopoverPosition = {
  /**
   * Bottom của popover gắn vào Top của target element (chỉ dùng cho trục y)
   */
  Top: 'top',

  /**
   * Top của popover gắn vào Bottom của target element (chỉ dùng cho trục y)
   */
  Bottom: 'bottom',

  /**
   * Left của popover gắn vào Left của target element (chỉ dùng cho trục x)
   */
  Left: 'left',

  /**
   * Right của popover gắn vào Right của target element (chỉ dùng cho trục x)
   */
  Right: 'right',

  /**
   * Popover nằm ở giữa so với target element tùy theo trục đặt biến này (dùng cho cả trục x và y)
   */
  Center: 'center',
};

export default {
  name: 'MsPopoverGlobal',
  components: {},
  props: {
    /**
     * Chiều rộng
     */
    width: {
      type: Number,
      default: 0,
    },
    /**
     * Chiều rộng tối đa
     */
    maxWidth: {
      type: Number,
      default: 0,
    },
    /**
     * Chiều rộng tối thiểu
     */
    minWidth: {
      type: Number,
      default: 0,
    },
    /**
     * Chiều cao
     */
    height: {
      type: Number,
      default: 0,
    },
    /**
     * Chiều cao tối đa
     */
    maxHeight: {
      type: Number,
      default: 0,
    },
    /**
     * Chiều cao tối thiểu
     */
    minHeight: {
      type: Number,
      default: 0,
    },
    /**
     * Target element cần truyền vào để xác định vị trí xuất hiện popover trên DOM.
     * Hiện tại ko cần dùng đến
     */
    target: {
      type: null,
      default: false,
      validator(val) {
        const type = typeof val;

        if (type === 'boolean' || type === 'string') return true;

        return val.nodeType === Node.ELEMENT_NODE;
      },
    },
    /**
     * Vị trí xuất hiện.
     * x: vị trí đặt popover vào trục x của target element left, center, right
     * y: vị trí đặt popover vào trục y của target element top, center, bottom
     * offsetX: margin-left của popover so với trục x
     * offsetY: margin-top của popover so với trục y
     * isOuterX: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục X ngược lại hướng vào trong
     * isOuterY: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục Y ngược lại hướng vào trong
     */
    position: {
      type: Object,
      default: () => ({
        x: MsPopoverPosition.Left,
        y: MsPopoverPosition.Bottom,
        offsetX: 0,
        offsetY: 0,
        isOuterX: false,
        isOuterY: true,
      }),
      validator(val) {
        if (
          // offsetX chỉ cho phép số
          typeof val.offsetX !== 'number' ||
          // offsetY chỉ cho phép số
          typeof val.offsetY !== 'number' ||
          // Trục X chỉ nhận giá trị left, center, right
          (val.x !== MsPopoverPosition.Left &&
            val.x !== MsPopoverPosition.Right &&
            val.x !== MsPopoverPosition.Center) ||
          // Trục Y chỉ nhận giá trị top, center, bottom
          (val.y !== MsPopoverPosition.Top &&
            val.y !== MsPopoverPosition.Bottom &&
            val.y !== MsPopoverPosition.Center) ||
          // isOuterX chỉ nhận kiểu boolean
          typeof val.isOuterX !== 'boolean' ||
          // isOuterY chỉ nhận kiểu boolean
          typeof val.isOuterY !== 'boolean'
        ) {
          return false;
        }
        return true;
      },
    },
    /**
     * Style của popover
     */
    popoverStyle: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Class của popover
     */
    popoverClass: {
      type: String,
      default: '',
    },

    // Có tự đống đánh z-index hay ko
    zIndexAuto: { type: Boolean, default: true },

    // z-index cơ sở popover sẽ có z-index từ số cơ sở này trở lên
    zIndexBase: { type: [String, Number], default: 1000 },

    // Truyền vào z-index tĩnh nếu ko dùng tự động đánh z-index
    zIndex: { type: [Boolean, String, Number], default: false },

    // Truyền tên transition để áp dụng transition (transition đc định nghĩa trong file transitions.scss)
    // Có thể truyền theo object để bind lên
    // Cần truyền width và height để áp dụng transition
    transition: { type: [Object, String], default: "" },
  },
  setup(props) {
    /**
     * Tính style của content element cụ thể là chiều rộng và chiều cao
     */
    const styles = computed(() => {
      const style = {
        ...props.popoverStyle,
      };
      if (props.width) style.width = `${props.width}px`;
      if (props.maxWidth) style.maxWidth = `${props.maxWidth}px`;
      if (props.minWidth) style.minWidth = `${props.minWidth}px`;
      if (props.height) style.height = `${props.height}px`;
      if (props.maxHeight) style.maxHeight = `${props.maxHeight}px`;
      if (props.minHeight) style.minHeight = `${props.minHeight}px`;
      return style;
    });

    return {
      styles,
    };
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/_variables.scss';
@import '@/assets/scss/function.scss';

.popover__content {
  background-color: var(--white);
  box-shadow: 0 0 16px 0 rgba($color: var(--black-shadown), $alpha: 0.24);
  padding: 8px;
  border-radius: 4px;
  overflow: auto;
}
</style>
